import React from 'react';
import { Box, Stack, Typography, Drawer, IconButton, Button, Paper } from 'omaha-design-system';
import { X as CloseIcon, Plus as AddIcon } from '@phosphor-icons/react';
import { PlayerPropSportsbook, BetSlipItem } from '../data/types';
import { SportsbookLogo } from './SportsbookLogo';

interface OtherBooksBottomSheetProps {
  open: boolean;
  onClose: () => void;
  playerName: string;
  playerId: string;
  avatar: string;
  stat: string;
  side: 'Over' | 'Under';
  projection: number;
  sportsbooks: PlayerPropSportsbook[];
  onAddToBetSlip: (bet: BetSlipItem) => void;
  betSlip: BetSlipItem[];
}

export const OtherBooksBottomSheet: React.FC<OtherBooksBottomSheetProps> = ({
  open,
  onClose,
  playerName,
  playerId,
  avatar,
  stat,
  side,
  projection,
  sportsbooks,
  onAddToBetSlip,
  betSlip,
}) => {
  // Skip first sportsbook (best book shown on card)
  const otherBooks = sportsbooks.slice(1);

  const handleAddBook = (sb: PlayerPropSportsbook) => {
    onAddToBetSlip({
      id: `${playerId}-${stat}-${sb.book}`,
      playerName,
      avatar,
      stat,
      projection: projection.toFixed(1),
      bookLine: sb.line.toFixed(1),
      edge: sb.edge.toFixed(1),
      bookWin: sb.bookWin,
      modelWin: sb.modelWin,
      evPct: sb.evPct,
      book: sb.book,
      bookLogo: sb.bookIcon,
      odds: sb.americanOdds,
      side: sb.side,
      deepLinkUrl: sb.deepLinkUrl,
    });
  };

  const isInBetSlip = (sb: PlayerPropSportsbook) => {
    const betId = `${playerId}-${stat}-${sb.book}`;
    return betSlip.some((item) => item.id === betId);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: '#212223',
          borderRadius: '16px 16px 0 0',
          maxHeight: '80vh',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header with grabber and close button */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          {/* Grabber */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 32,
              height: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: 2,
            }}
          />
          <Box sx={{ width: 40 }} /> {/* Spacer */}
          <IconButton onClick={onClose} sx={{ color: '#F2F2F3' }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Title and subtitle */}
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 700,
              fontSize: '23.04px',
              lineHeight: 1.248,
              color: '#F2F2F3',
            }}
          >
            Other Books
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#F2F2F3',
              }}
            >
              {playerName}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: 1.5,
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              {stat} {side}
            </Typography>
          </Stack>
        </Stack>

        {/* Book cards list */}
        <Stack spacing={1.5} sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {otherBooks.map((sb) => {
            const inSlip = isInBetSlip(sb);
            const evPct = sb.evPct ?? 0;
            const isPositiveEv = evPct >= 0;

            return (
              <Paper
                key={sb.book}
                sx={{
                  backgroundColor: '#2A2B2C',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  p: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {/* Left side: Book info */}
                  <Stack direction="row" alignItems="center" gap={2}>
                    {/* Book icon */}
                    <SportsbookLogo book={sb.book} bookIcon={sb.bookIcon} size={32} borderRadius="6px" />
                    {/* Book details */}
                    <Stack>
                      <Typography
                        sx={{
                          fontFamily: '"Instrument Sans", sans-serif',
                          fontWeight: 600,
                          fontSize: '13.33px',
                          lineHeight: 1.5,
                          color: '#F2F2F3',
                        }}
                      >
                        {sb.book}
                      </Typography>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography
                          sx={{
                            fontFamily: '"Instrument Sans", sans-serif',
                            fontWeight: 400,
                            fontSize: '11.11px',
                            lineHeight: 1.22,
                            color: 'rgba(242, 242, 243, 0.7)',
                          }}
                        >
                          {sb.side === 'Under' ? 'u' : 'o'}{sb.line.toFixed(1)} ({sb.americanOdds})
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Instrument Sans", sans-serif',
                            fontWeight: 400,
                            fontSize: '11.11px',
                            lineHeight: 1.22,
                            color: isPositiveEv ? '#66BB6A' : '#EF5350',
                          }}
                        >
                          {isPositiveEv ? '+' : ''}{evPct.toFixed(1)}%
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>

                  {/* Right side: Add button */}
                  <Button
                    variant={inSlip ? 'outlined' : 'outlined'}
                    size="small"
                    startIcon={<AddIcon size={14} />}
                    disabled={inSlip}
                    onClick={() => handleAddBook(sb)}
                    sx={{
                      minWidth: 'auto',
                      height: 32,
                      borderRadius: '6px',
                      border: inSlip ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #66BB6A',
                      backgroundColor: 'transparent',
                      fontFamily: '"Instrument Sans", sans-serif',
                      fontWeight: 600,
                      fontSize: '11.11px',
                      color: inSlip ? 'rgba(255, 255, 255, 0.3)' : '#66BB6A',
                      textTransform: 'none',
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: inSlip ? 'transparent' : 'rgba(102, 187, 106, 0.08)',
                        border: inSlip ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #66BB6A',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    {inSlip ? 'Added' : 'Add'}
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default OtherBooksBottomSheet;
